import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import {
    Box,
    TextField,
    Typography,
    Chip,
    CircularProgress,
    Alert,
    Autocomplete
} from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store/store'
import { useGetCustomerMealCardsQuery } from '../../services/services'

interface MealCard {
    id: number;
    customerId: number;
    customerName: string | null;
    cardNo: string;
    status: string;
    productCode: string;
    walletNo: string | null;
    idNo: string | null;
    phoneNumber: string | null;
}

interface UserInfo {
    companyId: number;
    customerId: number;
}

interface CancelCardFormProps {
    onClose: () => void
}

export interface CancelCardFormRef {
    getFormData: () => {
        userData: UserInfo | null
        selectedCards: MealCard[]
        cancelReasonDescription: string
    }
    validateForm: () => boolean
}

const CancelCardForm = forwardRef<CancelCardFormRef, CancelCardFormProps>(({ onClose }, ref) => {
    const [selectedCards, setSelectedCards] = useState<MealCard[]>([])
    const [filteredCards, setFilteredCards] = useState<MealCard[]>([])
    const [cancelReasonDescription, setCancelReasonDescription] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const [error, setError] = useState('')

    // Redux'tan kullanıcı bilgilerini al
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState)

    // Meal kartları API'den çek - customerId kullan
    const { data: mealCardsData, isLoading: loading, error: apiError } = useGetCustomerMealCardsQuery(
        { customerId: currentUserState?.companyId || 0 },
        { skip: !currentUserState?.companyId }
    )

    // API'den gelen veriyi işle
    useEffect(() => {
        if (mealCardsData?.data) {
            // Kullanıcıya sadece "Aktif", "Beklemede" ve "Pasif" kartları göstertiyoruz
            const availableCards = mealCardsData.data.filter(card =>
                card.status === "Aktif" || card.status === "Beklemede" || card.status === "Pasif"
            )
            setFilteredCards(availableCards)
        }
    }, [mealCardsData])

    // API hatası varsa göster
    useEffect(() => {
        if (apiError) {
            setError('Kartlar yüklenirken bir hata oluştu. Lütfen tekrar deneyin.')
        }
    }, [apiError])

    // Kart arama fonksiyonu - API'den gelen verilerde arama yap
    const handleCardSearch = (event: React.SyntheticEvent, value: string) => {
        setSearchValue(value)
        if (!mealCardsData?.data) return

        if (!value) {
            const availableCards = mealCardsData.data.filter(card =>
                card.status === "Aktif" || card.status === "Beklemede" || card.status === "Pasif"
            )
            setFilteredCards(availableCards)
            return
        }

        const filtered = mealCardsData.data.filter(card =>
            (card.status === "Aktif" || card.status === "Beklemede" || card.status === "Pasif") &&
            (card.cardNo.toLowerCase().includes(value.toLowerCase()) ||
                (card.productCode && card.productCode.toLowerCase().includes(value.toLowerCase())))
        ).sort((a, b) => a.cardNo.localeCompare(b.cardNo))

        setFilteredCards(filtered)
    }

    // Kart label formatı - sadece cardNo ve productCode kullan
    const formatCardLabel = (card: MealCard): string => {
        if (card.productCode) {
            return `${card.cardNo} - ${card.productCode}`
        }
        return card.cardNo
    }

    const getFormattedCardMessage = (): string => {
        if (!selectedCards || selectedCards.length === 0) {
            return ''
        }

        if (selectedCards.length === 1) {
            return `Seçili kart: ${selectedCards[0].cardNo}`
        } else {
            return `${selectedCards.length} kart seçildi`
        }
    }

    // Form verilerini dışarıya açmak için ref
    useImperativeHandle(ref, () => ({
        getFormData: () => {
            const userData: UserInfo = {
                companyId: currentUserState?.companyId || 0,
                customerId: currentUserState?.customerId || 0
            }
            return {
                userData,
                selectedCards,
                cancelReasonDescription: cancelReasonDescription.trim()
            }
        },
        validateForm: () => {
            if (!currentUserState?.customerId) {
                setError('Kullanıcı bilgileri bulunamadı')
                return false
            }

            if (selectedCards.length === 0) {
                setError('Lütfen en az bir kart seçin')
                return false
            }

            if (!cancelReasonDescription.trim()) {
                setError('Lütfen iptal açıklaması girin')
                return false
            }

            setError('')
            return true
        }
    }))

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Kart Seçimi */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Kart Numarası
                </Typography>
                <Autocomplete
                    multiple
                    options={filteredCards}
                    getOptionLabel={formatCardLabel}
                    value={selectedCards}
                    onChange={(event, newValue) => setSelectedCards(newValue)}
                    onInputChange={handleCardSearch}
                    loading={loading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Kart arayın veya seçin"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <Box component="li" {...props}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', py: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {option.cardNo}
                                </Typography>
                                {option.productCode && (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2 }}>
                                        {option.productCode}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {option.cardNo}
                                        </Typography>
                                        {option.productCode && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                                - {option.productCode}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                {...getTagProps({ index })}
                                key={option.cardNo}
                            />
                        ))
                    }
                    ChipProps={{
                        size: 'small',
                        color: 'primary'
                    }}
                    sx={{ width: '100%' }}
                />

                {selectedCards.length > 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                        {getFormattedCardMessage()}
                    </Typography>
                )}
            </Box>

            {/* Açıklama Alanı */}
            <Box sx={{ mb: 0 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Açıklama
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder="Açıklama girin"
                    value={cancelReasonDescription}
                    onChange={(e) => setCancelReasonDescription(e.target.value)}
                    error={!cancelReasonDescription.trim() && selectedCards.length > 0}
                    helperText={!cancelReasonDescription.trim() && selectedCards.length > 0 ? 'Lütfen geçerli bir açıklama giriniz.' : ''}
                />
            </Box>
        </Box>
    )
})

export default CancelCardForm
